/**
 * @private
 */
var FALLBACK_LOCAL_STORAGE = new Map();
/**
 * @private
 */
var FALLBACK_SESSION_STORAGE = new Map();
/**
 * @private
 */
function isString(x) {
    return typeof x === 'string';
}
/**
 * Returns if the browser currently supports accessing the `localtorage`
 * API.
 *
 * @returns true if the native `localStorage` can be used, false if a
 *  fallbace map must be used instead
 *
 * @example
 * ```js
 * import { supportsNativeLocalStorage } from '@square/safe-storage';
 *
 * supportsNativeLocalStorage(); //=> true
 * ```
 *
 * @since 1.0.0
 */
function supportsNativeLocalStorage() {
    try {
        var hasLocalStorage = typeof window.localStorage !== 'undefined';
        return hasLocalStorage;
    }
    catch (e) {
        return false;
    }
}
/**
 * Returns if the browser currently supports accessing the `sessionStorage`
 * API.
 *
 * @returns true if the native `sessionStorage` can be used, false if a
 *  fallbace map must be used instead
 *
 * @example
 * ```js
 * import { supportsNativeSessionStorage } from '@square/safe-storage';
 *
 * supportsNativeSessionStorage(); //=> true
 * ```
 *
 * @since 1.0.0
 */
function supportsNativeSessionStorage() {
    try {
        var hasSessionStorage = typeof window.sessionStorage !== 'undefined';
        return hasSessionStorage;
    }
    catch (e) {
        return false;
    }
}
/**
 * Clears all keys stored in the `localStorage` object or a fallback map if
 * `localStorage` is unavailable
 *
 * @example
 * ```js
 * import { localClear } from '@square/safe-storage';
 *
 * localClear();
 * ```
 *
 * @since 1.0.0
 */
function localClear() {
    if (supportsNativeLocalStorage()) {
        window.localStorage.clear();
    }
    else {
        FALLBACK_LOCAL_STORAGE.clear();
    }
}
/**
 * Returns that key's value, or null if the key does not exist, in the
 * `localStorage` object or a fallback map if `localStorage` is unavailable
 *
 * @param key a string containing the name of the key you want to
 *  retrieve the value of
 * @param options an options object to control how to read the object from local storage.
 * @returns a string containing the value of the key. If the key
 *  does not exist, null is returned
 *
 * @example
 * ```js
 * import { localGet } from '@square/safe-storage';
 *
 * localGet('my-key'); //=> 'my-value'
 * localGet('my-bool-key', { coerce: true }); //=> true
 * localGet('my-num-key', { coerce: true }); //=> 2
 * ```
 *
 * @since 1.0.0
 */
function localGet(key, options) {
    if (!isString(key)) {
        throw new Error("[SafeStorage] Expected string key, got '" + key + "'");
    }
    var value;
    if (supportsNativeLocalStorage()) {
        value = window.localStorage.getItem(key);
    }
    else {
        value = FALLBACK_LOCAL_STORAGE.get(key) || null;
    }
    if (value !== null && (options === null || options === void 0 ? void 0 : options.coerce)) {
        return JSON.parse(value);
    }
    return value;
}
/**
 * Removes the key/value pair with the given key from the `localStorage` object
 * or a fallback map if `localStorage` is unavailable, if a key/value pair with
 * the given key exists.
 *
 * @param key a string containing the name of the key you want to remove
 *
 * @example
 * ```js
 * import { localRemove } from '@square/safe-storage';
 *
 * localRemove('my-key');
 * ```
 *
 * @since 1.0.0
 */
function localRemove(key) {
    if (!isString(key)) {
        throw new Error("[SafeStorage] Expected string key, got '" + key + "'");
    }
    if (supportsNativeLocalStorage()) {
        window.localStorage.removeItem(key);
    }
    else {
        FALLBACK_LOCAL_STORAGE.delete(key);
    }
}
/**
 * When passed a key name and value, will add that key to the `localStorage`
 * object or a fallback map if `localStorage` is unavailable, or update that
 * key's value if it already exists
 *
 * @param key a string containing the name of the key you want to create/update
 * @param value a string containing the value you want to give the key you are
 *  creating/updating
 *
 * @example
 * ```js
 * import { localSet } from '@square/safe-storage';
 *
 * localSet('my-key', 'my-value');
 * localSet('my-bool-key', true, { coerce: true });
 * localSet('my-num-key', 2, { coerce: true });
 * ```
 *
 * @since 1.0.0
 */
function localSet(key, value, options) {
    if (!isString(key)) {
        throw new Error("[SafeStorage] Expected string key, got '" + key + "'");
    }
    if (!isString(value) && !(options === null || options === void 0 ? void 0 : options.coerce)) {
        throw new Error("[SafeStorage] Expected string value, got '" + value + "'. Pass { coerce: true } to store non-string values.");
    }
    if (options === null || options === void 0 ? void 0 : options.coerce) {
        value = JSON.stringify(value);
    }
    if (supportsNativeLocalStorage()) {
        window.localStorage.setItem(key, value);
    }
    else {
        FALLBACK_LOCAL_STORAGE.set(key, value);
    }
}
/**
 * Clears all keys stored in the `sessionStorage` object or a fallback map if
 * `sessionStorage` is unavailable
 *
 * @example
 * ```js
 * import { sessionClear } from '@square/safe-storage';
 *
 * sessionClear();
 * ```
 *
 * @since 1.0.0
 */
function sessionClear() {
    if (supportsNativeSessionStorage()) {
        window.sessionStorage.clear();
    }
    else {
        FALLBACK_SESSION_STORAGE.clear();
    }
}
/**
 * Returns that key's value, or null if the key does not exist, in the
 * `sessionStorage` object or a fallback map if `sessionStorage` is unavailable
 *
 * @param key a string containing the name of the key you want to
 *  retrieve the value of
 * @param options an options object to control how to read the value
 * @returns a string containing the value of the key. If the key
 *  does not exist, null is returned
 *
 * @example
 * ```js
 * import { sessionGet } from '@square/safe-storage';
 *
 * sessionGet('my-key'); //=> 'my-value'
 * sessionGet('my-bool-key', { coerce: true }); //=> true
 * sessionGet('my-num-key', { coerce: true }); //=> 2
 * ```
 *
 * @since 1.0.0
 */
function sessionGet(key, options) {
    if (!isString(key)) {
        throw new Error("[SafeStorage] Expected string key, got '" + key + "'");
    }
    var value;
    if (supportsNativeSessionStorage()) {
        value = window.sessionStorage.getItem(key);
    }
    else {
        value = FALLBACK_SESSION_STORAGE.get(key) || null;
    }
    if (value !== null && (options === null || options === void 0 ? void 0 : options.coerce)) {
        return JSON.parse(value);
    }
    return value;
}
/**
 * Removes the key/value pair with the given key from the `sessionStorage`
 * object or a fallback map if `sessionStorage` is unavailable, if a key/value
 * pair with the given key exists.
 *
 * @param key a string containing the name of the key you want to remove
 *
 * @example
 * ```js
 * import { sessionRemove } from '@square/safe-storage';
 *
 * sessionRemove('my-key');
 * ```
 *
 * @since 1.0.0
 */
function sessionRemove(key) {
    if (!isString(key)) {
        throw new Error("[SafeStorage] Expected string key, got '" + key + "'");
    }
    if (supportsNativeSessionStorage()) {
        window.sessionStorage.removeItem(key);
    }
    else {
        FALLBACK_SESSION_STORAGE.delete(key);
    }
}
/**
 * When passed a key name and value, will add that key to the `sessionStorage`
 * object or a fallback map if `sessionStorage` is unavailable, or update that
 * key's value if it already exists
 *
 * @param key a string containing the name of the key you want to create/update
 * @param value a string containing the value you want to give the key you are
 *  creating/updating
 * @param options an options object to control how to persist the value
 *
 * @example
 * ```js
 * import { sessionSet } from '@square/safe-storage';
 *
 * sessionSet('my-key', 'my-value');
 * sessionSet('my-bool-key', { coerce: true }, true);
 * sessionSet('my-num-key', { coerce: true }, 2);
 * ```
 *
 * @since 1.0.0
 */
function sessionSet(key, value, options) {
    if (!isString(key)) {
        throw new Error("[SafeStorage] Expected string key, got '" + key + "'");
    }
    if (!isString(value) && !(options === null || options === void 0 ? void 0 : options.coerce)) {
        throw new Error("[SafeStorage] Expected string value, got '" + value + "'. Pass { coerce: true } to store non-string values.");
    }
    if (options === null || options === void 0 ? void 0 : options.coerce) {
        value = JSON.stringify(value);
    }
    if (supportsNativeSessionStorage()) {
        window.sessionStorage.setItem(key, value);
    }
    else {
        FALLBACK_SESSION_STORAGE.set(key, value);
    }
}

export { FALLBACK_LOCAL_STORAGE, FALLBACK_SESSION_STORAGE, localClear, localGet, localRemove, localSet, sessionClear, sessionGet, sessionRemove, sessionSet, supportsNativeLocalStorage, supportsNativeSessionStorage };
//# sourceMappingURL=safe-storage.es5.js.map
